import React, { useEffect, useState } from "react";
import moment from "moment";
import { Modal, Button, Spinner } from "react-bootstrap";
import {
  ArrowClockwise,
  BoxArrowRight,
  Calendar3,
  ClockHistory,
  HouseDoor,
  Person,
  PersonDash,
  PersonPlus,
} from "react-bootstrap-icons";
import { CustomerService } from "../../services";
import {
  ADMITTED_FROM_TEXT,
  CUSTOMER_DISCHARGE_REASON_TEXT,
  CUSTOMER_TYPE_TEXT,
} from "../../shared";

const EVENT_META = {
  admitted: {
    title: "Admitted",
    color: "#198754",
    Icon: HouseDoor,
  },
  discharged: {
    title: "Discharged",
    color: "#dc3545",
    Icon: BoxArrowRight,
  },
  readmitted: {
    title: "Readmitted",
    color: "#0d6efd",
    Icon: ArrowClockwise,
  },
  membership_activated: {
    title: "Membership Activated",
    color: "#fd7e14",
    Icon: PersonPlus,
  },
  membership_deactivated: {
    title: "Membership Deactivated",
    color: "#6f42c1",
    Icon: PersonDash,
  },
};

const formatHistoryDate = (value) => {
  if (value == null || String(value).trim() === "") return "—";
  const parsed = moment(
    value,
    ["MM/DD/YYYY", "M/D/YYYY", "YYYY-MM-DD", moment.ISO_8601],
    true
  );
  if (parsed.isValid()) return parsed.format("MMM D, YYYY");
  const fallback = moment(value);
  return fallback.isValid() ? fallback.format("MMM D, YYYY") : String(value);
};

const formatTypeLabel = (value) => {
  const key = `${value || ""}`.trim().toLowerCase();
  if (!key) return "";
  return CUSTOMER_TYPE_TEXT[key] || key.charAt(0).toUpperCase() + key.slice(1);
};

const formatAdmittedFrom = (row) => {
  const key = `${row?.admitted_from || ""}`.trim();
  if (!key) return "";
  const label = ADMITTED_FROM_TEXT[key] || key;
  if (key === "other" && row?.admitted_from_other) {
    return `${label} (${row.admitted_from_other})`;
  }
  return label;
};

const formatDischargeReason = (row) => {
  const key = `${row?.discharge_reason || ""}`.trim();
  if (!key) return "";
  const label = CUSTOMER_DISCHARGE_REASON_TEXT[key] || key;
  if (key === "other" && row?.discharge_reason_other) {
    return `${label} (${row.discharge_reason_other})`;
  }
  return label;
};

const TypePill = ({ value }) => {
  const label = formatTypeLabel(value);
  if (!label) return null;
  const kind = `${value || ""}`.trim().toLowerCase();
  return (
    <span className={`customer-history-type-pill is-${kind || "other"}`}>
      {label}
    </span>
  );
};

const EventDetail = ({ row }) => {
  const type = `${row?.event_type || ""}`.trim().toLowerCase();
  if (type === "membership_activated" || type === "membership_deactivated") {
    return (
      <div className="customer-history-event-detail">
        Membership Status: <TypePill value={row.from_type} />
        <span className="customer-history-arrow">→</span>
        <TypePill value={row.to_type} />
      </div>
    );
  }
  if (type === "discharged") {
    const reason = formatDischargeReason(row);
    return (
      <div className="customer-history-event-detail">
        {reason ? `Reason: ${reason}` : "Reason: —"}
      </div>
    );
  }
  if (type === "readmitted") {
    return (
      <div className="customer-history-event-detail">
        Admission Source: Returned after discharge
      </div>
    );
  }
  const source = formatAdmittedFrom(row);
  return (
    <div className="customer-history-event-detail">
      {source ? `Admission Source: ${source}` : "Admission Source: —"}
    </div>
  );
};

export const ViewHistoryButton = ({ onClick, className = "btn btn-outline-primary" }) => (
  <button type="button" className={className} onClick={onClick}>
    <ClockHistory className="me-2" size={16} aria-hidden />
    View History
  </button>
);

const CustomerChangeHistoryModal = ({ show, onHide, customerId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show || !customerId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    CustomerService.getCustomerChangeHistory(customerId)
      .then((res) => {
        if (cancelled) return;
        setRows(Array.isArray(res?.data) ? res.data : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setRows([]);
        setError(err?.response?.data?.message || "Failed to load history.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, customerId]);

  return (
    <Modal show={show} onHide={onHide} size="lg" className="customer-history-modal" centered>
      <Modal.Header closeButton>
        <div>
          <Modal.Title>View History</Modal.Title>
          <div className="customer-history-subtitle">
            A chronological record of this customer's admissions, discharges, and membership status changes.
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" />
          </div>
        )}
        {!loading && error && <div className="text-danger">{error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div className="text-muted">No history records yet.</div>
        )}
        {!loading && !error && rows.length > 0 && (
          <ol className="customer-history-timeline">
            {rows.map((row) => {
              const type = `${row?.event_type || ""}`.trim().toLowerCase();
              const meta = EVENT_META[type] || EVENT_META.admitted;
              const Icon = meta.Icon;
              return (
                <li key={row.id || row._id} className="customer-history-item">
                  <span
                    className="customer-history-dot"
                    style={{ backgroundColor: meta.color, color: meta.color }}
                  />
                  <div className="customer-history-card">
                    <div className="customer-history-event">
                      <span
                        className="customer-history-icon"
                        style={{ backgroundColor: `${meta.color}1a`, color: meta.color }}
                      >
                        <Icon size={16} aria-hidden />
                      </span>
                      <div>
                        <div className="customer-history-title" style={{ color: meta.color }}>
                          {meta.title}
                        </div>
                        <EventDetail row={row} />
                      </div>
                    </div>
                    <div className="customer-history-meta">
                      <div className="customer-history-meta-label">Effective Date</div>
                      <div className="customer-history-meta-value">
                        <Calendar3 size={14} className="me-1" aria-hidden />
                        {formatHistoryDate(row.effective_date)}
                      </div>
                    </div>
                    <div className="customer-history-meta">
                      <div className="customer-history-meta-label">Modified By</div>
                      <div className="customer-history-meta-value">
                        <Person size={14} className="me-1" aria-hidden />
                        {row.modified_by || "—"}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CustomerChangeHistoryModal;
